"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var EventDispatcher_1 = require("seng-event/lib/EventDispatcher");
var DeviceStateEvent_1 = require("./DeviceStateEvent");
/**
 * Utility class that tracks which media query is currently active using the
 * matchMedia API.
 */
var DeviceStateTracker = /** @class */ (function (_super) {
    tslib_1.__extends(DeviceStateTracker, _super);
    /**
     *
     * @param deviceStateConfig An object with media queries and device states
     * @param reverseDeviceStateOrder When targeting desktop first (max-width) media queries set the
     * reverseDeviceStateOrder boolean to true (default false)
     * @param showStateIndicator appends a div with the current state name
     */
    function DeviceStateTracker(deviceStateConfig) {
        var _this = _super.call(this) || this;
        /**
         * Array of MediaQueryList instances for each device state.
         */
        _this.queryList = [];
        /**
         * Array containing the name of each device state.
         */
        _this.deviceStateNames = [];
        /**
         * Array containing a boolean for each device state that indicates if the
         * media query currently matches. When multiple media queries match, it will
         * set the state to the one with the largest index. In reverse device state order
         * it will set the state to the one with the smallest index.
         */
        _this.queryListMatches = [];
        _this.enumCheck(deviceStateConfig.deviceState);
        _this.reverseDeviceStateOrder = deviceStateConfig.reverseDeviceStateOrder || false;
        _this.deviceState = deviceStateConfig.deviceState;
        _this.mediaQueries = deviceStateConfig.mediaQueries;
        if (deviceStateConfig.showStateIndicator) {
            _this.initStateIndicator();
        }
        _this.handleQueryChange = _this.handleQueryChange.bind(_this);
        _this.initTracking();
        return _this;
    }
    Object.defineProperty(DeviceStateTracker.prototype, "currentDeviceState", {
        /**
         * Get current deviceState
         * @returns {IDeviceStateData}
         */
        get: function () {
            return {
                state: this.currentState,
                name: this.currentStateName,
            };
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Checks if the enum object is correctly formatted. The DeviceState enum should only contain numeric values in
     * an ascending order.
     * @param deviceState
     */
    DeviceStateTracker.prototype.enumCheck = function (deviceState) {
        // Get all the keys of deviceState object
        var enumValues = Object.keys(deviceState).map(function (k) { return deviceState[k]; });
        // Get all the numeric values from enumValues
        var enumKeys = enumValues.filter(function (value) { return typeof value === 'number'; });
        // Check if we actually have enum values
        if (enumKeys.length === 0) {
            throw new Error("[DeviceStateTracker] DeviceState object should contain valid enum values");
        }
        // Check if enum keys are in an ascending order
        enumKeys.forEach(function (key, index) {
            // Check order
            if (key !== index) {
                throw new Error("[DeviceStateTracker] DeviceState " + deviceState[key] + ": " + key + " is not following an ascending order");
            }
        });
    };
    /**
     * Initializes tracking of media queries using matchMedia.
     */
    DeviceStateTracker.prototype.initTracking = function () {
        this.deviceStateNames = Object.keys(this.deviceState).filter(function (key) { return isNaN(parseInt(key, 10)); });
        this.initMatchMedia();
    };
    /**
     * Loops through each deviceState and adds a matchMedia listener for each.
     * Calls updateFromMatchMedia to initialize the current state.
     */
    DeviceStateTracker.prototype.initMatchMedia = function () {
        var _this = this;
        this.queryList = this.deviceStateNames.map(function (stateName) {
            var mediaQuery = _this.mediaQueries[stateName];
            if (!mediaQuery) {
                throw new Error("[DeviceStateTracker] DeviceState " + stateName + " not found in the mediaQueries array.");
            }
            return window.matchMedia(mediaQuery);
        });
        this.queryList.forEach(function (mql) {
            _this.queryListMatches.push(mql.matches);
            mql.addListener(_this.handleQueryChange);
        });
        this.updateFromMatchMedia();
    };
    /**
     * Called whenever a MediaQueryList updates. Checks if the query matches
     * and stores the result in the queryListMatches array. Then calls
     * updateFromMatchMedia() to find the current state from all matching
     * queries.
     * @param changedMql The MediaQueryList that changed
     */
    DeviceStateTracker.prototype.handleQueryChange = function (changedMql) {
        var _this = this;
        this.queryList.forEach(function (mql, index) {
            if (mql.media === changedMql.media) {
                _this.queryListMatches[index] = changedMql.matches;
            }
        });
        this.updateFromMatchMedia();
    };
    /**
     * Takes the results from the matchMedia event listeners saved in the
     * queryListMatches property. Sets the last one in the array as the active
     * query. When the reverseDeviceStateOrder boolean is set to true, will
     * set the first one in this array. Dispatches a DeviceStateEvent with the
     * current DeviceState
     */
    DeviceStateTracker.prototype.updateFromMatchMedia = function () {
        var numQueries = this.queryListMatches.length;
        for (var i = 0; i < numQueries; i += 1) {
            var index = this.reverseDeviceStateOrder ? i : numQueries - 1 - i;
            if (this.queryListMatches[index]) {
                this.currentState = index;
                this.currentStateName = this.deviceStateNames[index];
                if (this.stateIndicator) {
                    this.stateIndicator.textContent = this.currentStateName;
                }
                this.dispatchEvent(new DeviceStateEvent_1.default(DeviceStateEvent_1.default.STATE_UPDATE, {
                    state: index,
                    name: this.currentStateName,
                }));
                break;
            }
        }
    };
    /**
     * Initializes tracking of the current media query using a state indicator
     * element. This element will hold the state name as content inside.
     */
    DeviceStateTracker.prototype.initStateIndicator = function () {
        this.stateIndicator = document.createElement('div');
        this.stateIndicator.className = 'seng-state-indicator';
        var style = document.createElement('style');
        style.setAttribute('type', 'text/css');
        style.innerHTML = "\n\t\t\t.seng-state-indicator {\n\t\t\t  position: fixed;\n\t\t\t  bottom: 0;\n\t\t\t  left: 0;\n\t\t\t  background-color: #f00;\n\t\t\t  padding: 4px;\n\t\t\t  text-transform: uppercase;\n\t\t\t  font: normal small-caps 12px/1 monospace;\n\t\t\t  border-radius: 0 3px 0 0;\n\t\t\t  color: #fff;\n\t\t\t  border-right: 1px solid #000;\n\t\t\t  border-top: 1px solid #000;\n\t\t\t  pointer-events: none;\n\t\t\t}\n\t\t";
        // Always inject the CSS as first element in the document
        // head so it can be easily overridden by other style sheets
        document.head.insertBefore(style, document.head.children[0]);
        document.body.appendChild(this.stateIndicator);
    };
    /**
     * Destruct this DeviceStateTracker instance and remove any event listeners.
     */
    DeviceStateTracker.prototype.destruct = function () {
        var _this = this;
        this.queryList.forEach(function (query) {
            query.removeListener(_this.handleQueryChange);
        });
        if (this.stateIndicator) {
            document.body.removeChild(this.stateIndicator);
        }
        this.queryList.length = 0;
    };
    return DeviceStateTracker;
}(EventDispatcher_1.default));
exports.default = DeviceStateTracker;
